import {
	checkDeviceStatus,
	IDeviceCheckResponse,
	TDeviceScreen,
	TDeviceStatus,
	TDeviceType,
	TTypeAccess,
} from '@/services';
import {
	DeviceType as FrontendDeviceType,
	getDeviceType,
} from '@/utils/mobile.util';
import { useCallback, useEffect, useState } from 'react';

import { useFingerprint } from './useFingerprint.hook';

type UseDeviceCheckReturn = {
	deviceStatus: TDeviceStatus | null;
	screen: TDeviceScreen | null;
	deviceType: FrontendDeviceType;
	fingerprint: string | null;
	isLoading: boolean;
	isChecking: boolean;
	error: Error | null;
	tabletName?: string;
	validFrom?: string;
	validUntil?: string;
	rejectReason?: string;
	typeAccess?: TTypeAccess;
	refetch: () => Promise<void>;
};

type UseDeviceCheckOptions = {
	// If provided, only check when device type matches this value.
	// Prevents API calls when device type changes during redirect.
	expectedDeviceType?: 'mobile' | 'tablet';
};

/**
 * Hook to check device status and determine which screen to show
 * Combines fingerprint collection and device status API check
 * @param options - Optional configuration
 * @param options.expectedDeviceType - If provided, skips API check when device type doesn't match
 */
export const useDeviceCheck = (
	options?: UseDeviceCheckOptions
): UseDeviceCheckReturn => {
	const { expectedDeviceType } = options || {};

	const [deviceStatus, setDeviceStatus] = useState<TDeviceStatus | null>(null);
	const [screen, setScreen] = useState<TDeviceScreen | null>(null);
	const [isChecking, setIsChecking] = useState(true);
	const [error, setError] = useState<Error | null>(null);
	const [tabletName, setTabletName] = useState<string | undefined>();
	const [validFrom, setValidFrom] = useState<string | undefined>();
	const [validUntil, setValidUntil] = useState<string | undefined>();
	const [rejectReason, setRejectReason] = useState<string | undefined>();
	const [typeAccess, setTypeAccess] = useState<TTypeAccess | undefined>();

	// Reactive device type - updates on resize with debounce
	const [currentDeviceType, setCurrentDeviceType] =
		useState<FrontendDeviceType>(getDeviceType);

	useEffect(() => {
		let debounceTimer: ReturnType<typeof setTimeout>;
		const handleResize = () => {
			clearTimeout(debounceTimer);
			debounceTimer = setTimeout(() => {
				setCurrentDeviceType(getDeviceType());
			}, 150);
		};
		window.addEventListener('resize', handleResize);
		return () => {
			clearTimeout(debounceTimer);
			window.removeEventListener('resize', handleResize);
		};
	}, []);

	// Get fingerprint (only for mobile devices)
	const {
		fingerprint,
		isLoading: isFingerprintLoading,
		error: fingerprintError,
	} = useFingerprint(currentDeviceType === 'mobile');

	// Map device type to API device type
	const getApiDeviceType = useCallback((): TDeviceType | null => {
		if (currentDeviceType === 'mobile') return 'MOBILE';
		if (currentDeviceType === 'tablet') return 'TABLET';
		return null; // PC - not supported for device check
	}, [currentDeviceType]);

	// Check device status from server
	const checkDevice = useCallback(async () => {
		const apiDeviceType = getApiDeviceType();

		// PC does not need device check
		if (!apiDeviceType) {
			setIsChecking(false);
			return;
		}

		// For mobile, wait for fingerprint
		if (apiDeviceType === 'MOBILE' && isFingerprintLoading) {
			return;
		}

		try {
			setIsChecking(true);
			setError(null);

			const response = await checkDeviceStatus({
				fingerprint: fingerprint || undefined,
				deviceType: apiDeviceType,
			});

			const data: IDeviceCheckResponse = response.data;

			setDeviceStatus(data.deviceStatus);
			setScreen(data.screen);
			setTabletName(data.tabletName);
			setValidFrom(data.validFrom);
			setValidUntil(data.validUntil);
			setRejectReason(data.rejectReason);
			setTypeAccess(data.typeAccess);

			// If fingerprint was updated, we might want to log this
			if (data.fingerprintUpdated) {
				// TODO
			}
		} catch (err) {
			const errorObj =
				err instanceof Error ? err : new Error('Failed to check device status');
			setError(errorObj);
			// On error, treat as unregistered
			setDeviceStatus('UNREGISTERED');
			setScreen('REGISTER');
		} finally {
			setIsChecking(false);
		}
	}, [fingerprint, isFingerprintLoading, getApiDeviceType]);

	// Auto-check on mount and when fingerprint is ready
	useEffect(() => {
		const apiDeviceType = getApiDeviceType();

		// PC doesn't need device check
		if (!apiDeviceType) {
			setIsChecking(false);
			return;
		}

		// If expected device type is provided, skip check if current type doesn't match
		// This prevents API calls when device type changes during redirect
		if (expectedDeviceType) {
			const shouldSkip =
				(expectedDeviceType === 'mobile' && apiDeviceType !== 'MOBILE') ||
				(expectedDeviceType === 'tablet' && apiDeviceType !== 'TABLET');
			if (shouldSkip) {
				setIsChecking(false);
				return;
			}
		}

		// For mobile, wait for fingerprint to be ready
		if (apiDeviceType === 'MOBILE' && (isFingerprintLoading || !fingerprint)) {
			return;
		}

		// For tablet or when fingerprint is ready, check device
		checkDevice();
	}, [
		fingerprint,
		isFingerprintLoading,
		getApiDeviceType,
		checkDevice,
		expectedDeviceType,
	]);

	return {
		deviceStatus,
		screen,
		deviceType: currentDeviceType,
		fingerprint,
		isLoading: isFingerprintLoading,
		isChecking,
		error: error || fingerprintError,
		tabletName,
		validFrom,
		validUntil,
		rejectReason,
		typeAccess,
		refetch: checkDevice,
	};
};

export default useDeviceCheck;

// Device token key for localStorage
const DEVICE_TOKEN_KEY = 'X-Device-Token';

// Export utility functions for other components
export const getDeviceToken = (): string | null => {
	try {
		return localStorage.getItem(DEVICE_TOKEN_KEY);
	} catch {
		return null;
	}
};

export const saveDeviceToken = (token: string): void => {
	try {
		localStorage.setItem(DEVICE_TOKEN_KEY, token);
	} catch {
		console.error('[DeviceCheck] Failed to save device token');
	}
};

export const removeDeviceToken = (): void => {
	try {
		localStorage.removeItem(DEVICE_TOKEN_KEY);
	} catch {
		console.error('[DeviceCheck] Failed to remove device token');
	}
};
